import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { variables } from 'src/Tenants';
import { USER_ROLES } from 'src/common/constants';
import { onCheckUserCanAccessTheModule } from 'src/helpers/validationHelper';
import { onUrlImageExtensionExist } from 'src/helpers/CommonHelpers';
import {
  ExtractRoleIdResult,
  safelyExtractUserRoleId,
  User,
} from 'src/utils/safelyExtractUserRoleId';
import { useBranding } from 'src/branding/BrandingContextProvider';
import { useDocumentUpload } from 'src/pages/SharedPageCustomHooks/UseDocumentUpload/useDocumentUpload';
import { UploadedFile } from 'src/pages/SharedPageCustomHooks/UseDocumentUpload/helpers';
import {
  ServiceRequestFormContextValue,
  useServiceRequestFormContext,
} from 'src/components/ServiceRequestForm/CustomHooks/ServiceRequestFormContext';
import { ServiceRequestFormStep } from 'src/components/ServiceRequestForm/CustomHooks/serviceRequestFormSteps';
import { useServiceRequestSubmission } from 'src/components/ServiceRequestForm/ServiceRequestFormDetails/ServiceRequestFormDetailsCustomHooks/useServiceRequestSubmission';
import { ServiceRequestCreationNote } from 'src/components/ServiceRequestForm/ServiceRequestFormDetails/ServiceRequestFormDetailsCustomHooks/serviceRequestFormtypes';
import {
  isSubmitDisabledByTenantRules,
  shouldShowScheduling,
} from 'src/components/ServiceRequestForm/ServiceRequestFormDetails/detailsTenantRules';

interface TagOption {
  value: any;
  label: string;
}

// Mirrors AnnouncementForm's MAX_ATTACHMENTS cap (see useAnnouncementFormOrchestrator).
// TODO(annotation-s3): attachments are staged and uploaded to S3 one-by-one in a
// loop on submit today. Refactor the endpoint to accept them in a single batch and
// revisit this cap once that lands.
export const MAX_ATTACHMENTS: number = 5;

export interface UseServiceRequestFormDetailsOrchestratorReturn {
  diagnosisAsset: string;
  diagnosisIssueType: string;
  warrantyExpiresAt: string;
  propertyName: string;
  suiteNumber: string;
  descriptionPlaceholder: string;
  description: string;
  onChangeDescription: (value: string) => void;

  canManageTags: boolean;
  tagOptions: TagOption[];
  selectedTags: any[];
  onChangeTags: (newValue: any) => void;

  onSelectFiles: (files: any) => void;
  isAttachmentRequired: boolean;
  attachments: any[];
  onDeleteAttachment: (item: any, index: number) => void;
  maxAttachments: number;
  canAddMoreAttachments: boolean;

  canAnnotate: boolean;
  annotatableAttachments: any[];

  showScheduling: boolean;
  onChangeSchedule: (selected: any) => void;

  primarySubmitLabel: string;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  onSubmit: (
    type: string,
    annotations: Record<string, string>,
    note?: ServiceRequestCreationNote
  ) => void;

  onBack: () => void;
  onExit: () => void;
}

const TAG_MANAGER_ROLES: string[] = [
  USER_ROLES.PROPERTY_DEVELOPER,
  USER_ROLES.ADMIN,
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.SERVICE_MANAGER,
  USER_ROLES.STRATA_MANAGER,
];

export const useServiceRequestFormDetailsOrchestrator =
  (): UseServiceRequestFormDetailsOrchestratorReturn => {
    const {
      formData,
      patchFormData,
      goNext,
      goPrev,
      goToStep,
      seed,
      user,
      from,
      onExit,
      reference,
    }: ServiceRequestFormContextValue = useServiceRequestFormContext();

    const {
      properties,
      propertySuites,
      serviceRequestTags,
      getFaqsForIssueType,
      isLoadingFormOptions,
    } = reference;

    const { branding } = useBranding();

    const { submitServiceRequest, isSubmitting } = useServiceRequestSubmission();

    const { validateAndProcessFiles } = useDocumentUpload({
      documentType: ['Image', 'Video'],
      allowMultiple: true,
      maxSizeBytes: 25 * 1024 * 1024,
    });

    const [isDisablingDuringSubmit, setIsDisablingDuringSubmit] =
      useState<boolean>(false);

    const [userRole, setUserRole] = useState<string>('');

    const [tenantName, setTenantName] = useState<string>('');

    const theme: string = localStorage.getItem('theme') ?? '';

    const clearPreviousState = (): void => {
      localStorage.removeItem('isAnnotationImageUpload');
      patchFormData({ uploadedImages: [], showImageLoading: false });
    };

    const applyThemeDefaults = (): string => {
      const currentTheme: string = localStorage.getItem('theme') ?? '';

      if (currentTheme === 'Rize' || currentTheme === 'Formwerks') {
        patchFormData({ schedulePreferences: 'Monday' });
      }

      return currentTheme;
    };

    const setupUserRole = (): boolean => {
      const { data: roleId, error: roleError }: ExtractRoleIdResult<string> =
        safelyExtractUserRoleId(user as User);

      if (roleError) {
        console.error('Error extracting role ID:', roleError.message);
        toast.error(
          'Unable to verify your permissions. This may be a temporary issue or your account may need additional access. ' +
            'Please try refreshing the page or contact Juniper Support at support@juniper.re for assistance.',
          { position: 'top-center' }
        );
        return false;
      }

      if (roleId) {
        setUserRole(roleId);
      }

      return true;
    };

    useEffect(() => {
      clearPreviousState();

      const currentTheme: string = applyThemeDefaults();

      const roleSetupSuccessful: boolean = setupUserRole();

      if (!roleSetupSuccessful) {
        return;
      }

      setTenantName(currentTheme);
    }, []);

    const selectedProperty: any =
      properties.find((property: any) => property.id === formData.property_id) ??
      null;

    const selectedSuite: any =
      propertySuites.find((suite: any) => suite.id === formData.suite_id) ?? null;

    const attachments: any[] =
      formData.srAttachments && formData.srAttachments.length > 0
        ? formData.srAttachments
        : [];

    const showTimeslots: boolean = !(theme === 'Rize' || theme === 'Formwerks');

    const descriptionPlaceholder: string =
      branding.text.serviceRequestDescriptionHint ||
      'Add details so we can understand the issue better.';

    const canManageTags: boolean = onCheckUserCanAccessTheModule(TAG_MANAGER_ROLES);

    const tagOptions: TagOption[] =
      serviceRequestTags.length > 0
        ? serviceRequestTags.map((tag: any) => ({
            value: tag.id,
            label: tag.name,
          }))
        : [];

    const isAttachmentRequired: boolean =
      theme === 'Shape' || theme === 'ShapeRC' || theme === 'Wesgroup';

    const showScheduling: boolean = shouldShowScheduling(
      showTimeslots,
      tenantName,
      userRole
    );

    const isSubmittingOrUploading: boolean =
      isSubmitting || formData.showImageLoading === true;

    const isSubmitDisabled: boolean =
      !formData.description ||
      isLoadingFormOptions ||
      isDisablingDuringSubmit ||
      isSubmitDisabledByTenantRules(theme, tenantName, userRole, formData);

    const annotatableAttachments: any[] = attachments.filter(
      (attachment: any) =>
        onUrlImageExtensionExist(attachment.path ?? attachment.url ?? '')
    );

    const canAnnotate: boolean = annotatableAttachments.length > 0;

    const addSelectedAttachments = (files: any[]): void => {
      if (!(files && files.length > 0)) {
        return;
      }

      patchFormData({
        srAttachments: [...files, ...attachments],
        showImageLoading: false,
      });
    };

    const onSelectFiles = (files: any): void => {
      if (!files || files.length === 0) {
        return;
      }

      const validFiles: UploadedFile[] = validateAndProcessFiles(files);

      if (validFiles.length === 0) {
        return;
      }

      const remainingSlots: number = MAX_ATTACHMENTS - attachments.length;

      if (remainingSlots <= 0) {
        toast.error(
          `🚫 You can attach up to ${MAX_ATTACHMENTS} files per ${variables.serviceRequest.toLowerCase()}.`
        );
        return;
      }

      const stagedNames: string[] = attachments.map(
        (attachment: any) => attachment.name ?? attachment.path
      );

      const deduped: UploadedFile[] = validFiles.filter(
        (file: any) => !stagedNames.includes(file.name)
      );

      const accepted: UploadedFile[] = deduped.slice(0, remainingSlots);

      if (deduped.length > remainingSlots) {
        toast.error(
          `🚫 Only ${remainingSlots} more can be added — the limit is ${MAX_ATTACHMENTS} files per ${variables.serviceRequest.toLowerCase()}.`
        );
      }

      addSelectedAttachments(accepted);
    };

    const onDeleteAttachment = (item: any, index: number): void => {
      const remaining: any[] = attachments.filter(
        (attachment: any, attachmentIndex: number) => attachmentIndex !== index
      );

      patchFormData({ srAttachments: remaining });
    };

    const onChangeDescription = (value: string): void => {
      patchFormData({ description: value });
    };

    const onChangeTags = (newValue: any): void => {
      patchFormData({ tags: newValue });
    };

    const onChangeSchedule = (selected: any): void => {
      patchFormData({ schedulePreferences: selected });
    };

    const buildAddAnotherSeed = (): any => {
      if (from === 'Property') {
        return {
          property_id: formData.propertyId,
          propertyId: formData.propertyId,
        };
      }

      if (from === 'Suite') {
        return {
          property_id: formData.propertyId,
          propertyId: formData.propertyId,
          suite_id: formData.suiteId,
          suiteId: formData.suiteId,
        };
      }

      return {};
    };

    const navigateAfterSubmit = (type: string, srData: any): void => {
      if (type === 'AddAnother') {
        const addAnotherSeed: any = buildAddAnotherSeed();

        seed(addAnotherSeed);
        return;
      }

      localStorage.setItem('ticketId', srData.ticketId);
      localStorage.setItem('ticketNumber', srData.ticketNumber);
      localStorage.setItem('serviceRequestId', srData.id);

      goNext();
    };

    const onSubmit = async (
      type: string,
      annotations: Record<string, string>,
      note?: ServiceRequestCreationNote
    ): Promise<void> => {
      setIsDisablingDuringSubmit(true);
      localStorage.removeItem('isAnnotationImageUpload');

      patchFormData({ showImageLoading: true });

      try {
        const result: any = await submitServiceRequest(
          formData,
          from,
          annotations,
          note
        );

        patchFormData({ showImageLoading: false });

        if (result.success) {
          navigateAfterSubmit(type, result.data);
        }
      } catch (err) {
        console.error('Error during submission:', err);
        patchFormData({ showImageLoading: false });
      } finally {
        setIsDisablingDuringSubmit(false);
      }
    };

    const onBack = (): void => {
      if (formData.faq_id) {
        goPrev();
        return;
      }

      if (formData.category) {
        goToStep(ServiceRequestFormStep.Faqs);
        return;
      }

      const faqsForIssueType: any[] = getFaqsForIssueType(formData.issueType);

      if (faqsForIssueType.length > 0) {
        goToStep(ServiceRequestFormStep.Category);
        return;
      }

      goToStep(ServiceRequestFormStep.IssueType);
    };

    return {
      diagnosisAsset: formData.asset_item_nameDescription,
      diagnosisIssueType: formData.issueType,
      warrantyExpiresAt: formData.assetWarrantyExpiresAt,
      propertyName: selectedProperty?.name ?? '',
      suiteNumber: selectedSuite?.number ?? '',
      descriptionPlaceholder,
      description: formData.description ?? '',
      onChangeDescription,

      canManageTags,
      tagOptions,
      selectedTags: formData.tags ?? [],
      onChangeTags,

      onSelectFiles,
      isAttachmentRequired,
      attachments,
      onDeleteAttachment,
      maxAttachments: MAX_ATTACHMENTS,
      canAddMoreAttachments: attachments.length < MAX_ATTACHMENTS,

      canAnnotate,
      annotatableAttachments,

      showScheduling,
      onChangeSchedule,

      primarySubmitLabel: `Submit ${variables.serviceRequest}`,
      isSubmitting: isSubmittingOrUploading,
      isSubmitDisabled,
      onSubmit,

      onBack,
      onExit,
    };
  };